import React from 'react';
import {makeStyles} from '@material-ui/core/styles';
import {
	Grid,
	Paper,
	Typography,
	Box,
	Tooltip,
	IconButton,
} from '@material-ui/core';
import {
	Today,
	LocationOn,
	LocalShipping,
	CheckCircle,
	Schedule,
	AttachMoney,
	Refresh,
} from '@material-ui/icons';
import {useOrders} from '../context/OrdersContext';

const useStyles = makeStyles((theme) => ({
	summaryCard: {
		padding: theme.spacing(2.5),
		borderRadius: 12,
		boxShadow: '0 2px 8px rgba(0,0,0,0.08)',
		transition: 'all 0.3s ease',
		height: '100%',
		position: 'relative',
		overflow: 'hidden',
		cursor: 'pointer',
		'&:hover': {
			boxShadow: '0 4px 16px rgba(0,0,0,0.15)',
			transform: 'translateY(-2px)',
		},
	},
	iconWrapper: {
		width: 48,
		height: 48,
		borderRadius: 10,
		display: 'flex',
		alignItems: 'center',
		justifyContent: 'center',
		marginBottom: theme.spacing(1.5),
	},
	title: {
		fontSize: 12,
		fontWeight: 600,
		color: theme.palette.text.secondary,
		marginBottom: theme.spacing(0.5),
		textTransform: 'uppercase',
		letterSpacing: 0.5,
	},
	value: {
		fontSize: 28,
		fontWeight: 700,
		color: theme.palette.text.primary,
		lineHeight: 1.2,
	},
	subValue: {
		fontSize: 12,
		color: theme.palette.text.secondary,
		marginTop: theme.spacing(0.5),
		lineHeight: 1.4,
	},
	backgroundPattern: {
		position: 'absolute',
		top: -15,
		right: -15,
		opacity: 0.08,
		fontSize: 100,
	},
}));

const OrdersSummary = () => {
	const classes = useStyles();
	const {state, actions} = useOrders();
	const {orderStatusCount, loading, provinceFilter} = state;

	const summaryData = [
		{
			id: 'today',
			// The two courier slices partition every order in the range (statuses
			// 2-8), so this card is the range total -- not a "today" figure and not
			// an "to deliver" one. It opens All Status, which lists that same set.
			title: 'Orders In Range',
			value: (() => {
				const orders = (orderStatusCount.ordersForDeliveryToday || 0) + (orderStatusCount.provinceOrders || 0);
				const items = (orderStatusCount.itemsForDeliveryToday || 0) + (orderStatusCount.provinceItems || 0);
				return `${orders.toLocaleString()} / ${items.toLocaleString()}`;
			})(),
			icon: Today,
			color: '#1976d2',
			bgColor: '#e3f2fd',
			subValue: 'Orders / Items in selected range',
			onClick: () => actions.setStatusFilter('2345678'),
		},
		{
			id: 'province',
			title: 'Province',
			value: (() => {
				const orders = orderStatusCount.provinceOrders || 0;
				const items = orderStatusCount.provinceItems || 0;
				return `${orders.toLocaleString()} / ${items.toLocaleString()}`;
			})(),
			icon: LocationOn,
			color: '#7b1fa2',
			bgColor: '#f3e5f5',
			subValue: 'Orders / Items',
			onClick: () => {
				actions.setProvinceFilter(provinceFilter === '1' ? 'all' : '1');
			},
		},
		{
			id: 'in-transit',
			title: 'In Transit',
			value: (orderStatusCount.inTransitCount || 0).toLocaleString(),
			icon: LocalShipping,
			color: '#0277bd',
			bgColor: '#e1f5fe',
			subValue: 'On the way + Arrived',
			onClick: () => actions.setStatusFilter('58'),
		},
		{
			id: 'delivered',
			title: 'Delivered',
			value: (() => {
				const delivered = orderStatusCount.delivered || 0;
				const total = orderStatusCount.all || 0;
				return `${delivered.toLocaleString()} / ${total.toLocaleString()}`;
			})(),
			icon: CheckCircle,
			color: '#2e7d32',
			bgColor: '#e8f5e9',
			subValue: 'Delivered / Total in range',
			onClick: () => actions.setStatusFilter('6'),
		},
		{
			id: 'late',
			title: 'Late Orders',
			value: (orderStatusCount.lateOrders || 0).toLocaleString(),
			icon: Schedule,
			color: '#e65100',
			bgColor: '#fff3e0',
			subValue: 'Delivered past timeslot',
			onClick: () => actions.setStatusFilter('late-orders'),
		},
		{
			id: 'unpaid',
			title: 'Unpaid',
			// Money actually owed, over the same orders the Pending Payments list
			// returns. Overpaid orders are reported by overdueTotal/overdueCount and
			// deliberately excluded here -- they are not a receivable.
			value: `$${(orderStatusCount.pendingPaymentsTotal || 0).toLocaleString(undefined, {minimumFractionDigits: 2, maximumFractionDigits: 2})}`,
			icon: AttachMoney,
			color: '#c62828',
			bgColor: '#ffebee',
			subValue: `${orderStatusCount.pendingPaymentsCount || 0} orders with outstanding balance`,
			onClick: () => actions.setStatusFilter('pending-payments'),
		},
	];

	return (
		<Box>
			<Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
				<Typography variant="h5" style={{fontWeight: 700}}>
					Orders Dashboard
				</Typography>
			<Tooltip title="Refresh data">
				<IconButton
					onClick={actions.softRefresh}
					disabled={loading}
					size="small"
				>
					<Refresh/>
				</IconButton>
			</Tooltip>
			</Box>

			<Grid container spacing={2}>
				{summaryData.map((item) => {
					const Icon = item.icon;
					return (
						<Grid item xs={12} sm={6} md={4} lg={2} key={item.id}>
							<Paper
								className={classes.summaryCard}
								elevation={0}
								onClick={item.onClick}
							>
								<Icon
									className={classes.backgroundPattern}
									style={{color: item.color}}
								/>

								<Box
									className={classes.iconWrapper}
									style={{backgroundColor: item.bgColor}}
								>
									<Icon style={{fontSize: 24, color: item.color}}/>
								</Box>

								<Typography className={classes.title}>
									{item.title}
								</Typography>

								<Typography className={classes.value}>
									{item.value}
								</Typography>

								{item.subValue && (
									<Typography className={classes.subValue}>
										{item.subValue}
									</Typography>
								)}
							</Paper>
						</Grid>
					);
				})}
			</Grid>
		</Box>
	);
};

export default OrdersSummary;
